import { DownloadPdfData } from '@/dc-shared-web/src/hooks/api/document/postDownloadPdf'
import type {
  Annotation,
  AnnotationsUnion,
  LinkAnnotation,
} from '@nutrient-sdk/viewer'

const getUpdatedAction = (
  PSPDFKit: (typeof import('@nutrient-sdk/viewer'))['default'],
  annotation: LinkAnnotation,
  offsets: Record<string, { first: number; last: number }>
) => {
  const target = annotation.action.uri.replace('page://', '')

  const [targetPageName, offset] = target.startsWith('checklist')
    ? [target.split(':')[0], '0']
    : target.split(':')

  return offsets[targetPageName]
    ? new PSPDFKit.Actions.GoToAction({
        pageIndex:
          offsets[targetPageName].first + (offset ? parseInt(offset, 10) : 0),
      })
    : undefined
}

const processPageAnnotation = (
  PSPDFKit: (typeof import('@nutrient-sdk/viewer'))['default'],
  annotation: Annotation,
  updatedAnnotations: Annotation[],
  removedAnnotations: Annotation[],
  offsets: Record<string, { first: number; last: number }>
) => {
  if (
    !(annotation instanceof PSPDFKit.Annotations.LinkAnnotation) ||
    !(annotation.action instanceof PSPDFKit.Actions.URIAction) ||
    !annotation.action.uri.startsWith('page://') ||
    annotation.action.uri.startsWith('page://dc-')
  ) {
    return
  }

  const action = getUpdatedAction(PSPDFKit, annotation, offsets)

  if (action) {
    updatedAnnotations.push(annotation.set('action', action))
  } else {
    removedAnnotations.push(annotation)
  }
}

const isPageMarkerLink = (
  PSPDFKit: (typeof import('@nutrient-sdk/viewer'))['default'],
  annotation: AnnotationsUnion
): annotation is LinkAnnotation =>
  annotation instanceof PSPDFKit.Annotations.LinkAnnotation &&
  annotation.action instanceof PSPDFKit.Actions.URIAction &&
  annotation.action.uri.includes('dc-')

const extractPageInformation = (
  uri: string
): {
  name: string | null
  type: 'first' | 'last'
} => {
  const match = uri.match(/page:\/\/dc-(.+)-(start|end)/)

  return match
    ? {
        name: match[1] ?? null,
        type: match[2] === 'start' ? 'first' : 'last',
      }
    : { name: null, type: 'first' }
}

const processPagerMarkerLink = (
  annotation: LinkAnnotation,
  data: DownloadPdfData
) => {
  const { name, type } = extractPageInformation(annotation.action.uri)

  if (!name) {
    return
  }

  if (!data.offsets[name]) {
    data.offsets[name] = { first: 0, last: 0 }
  }

  data.offsets[name][type] = annotation.pageIndex
}

export const updateLinkAnnotations = async (
  licenseKey: string | undefined,
  data: DownloadPdfData
) => {
  const { default: PSPDFKit } = await import('@nutrient-sdk/viewer')
  const instance = await PSPDFKit.load({
    container: '',
    document: await data.pdf.arrayBuffer(),
    headless: true,
    licenseKey,
    baseUrl: `${window.location.origin}/nextassets/`,
  })

  const updatedAnnotations: Annotation[] = []
  const removedAnnotations: Annotation[] = []

  const totalPages = instance.totalPageCount
  for (let page = 0; page < totalPages; page++) {
    const annotations = await instance.getAnnotations(page)
    for (const annotation of annotations) {
      if (isPageMarkerLink(PSPDFKit, annotation)) {
        processPagerMarkerLink(annotation, data)
      }
    }
  }

  // find max of all offset ends (besides 'document') and set document start to max
  if (data.offsets.document) {
    const offsets = Object.keys(data.offsets).filter(
      (key) => key !== 'document'
    )
    const maxOffset = Math.max(...offsets.map((key) => data.offsets[key].last))
    data.offsets.document.first = maxOffset + 1
  }

  for (const page of Object.keys(data.offsets)) {
    for (
      let pageIndex = data.offsets[page].first;
      pageIndex <= data.offsets[page].last && pageIndex < totalPages;
      pageIndex++
    ) {
      const annotations = await instance.getAnnotations(pageIndex)
      for (const annotation of annotations) {
        processPageAnnotation(
          PSPDFKit,
          annotation,
          updatedAnnotations,
          removedAnnotations,
          data.offsets
        )
      }
    }
  }

  await Promise.all([
    instance.update(updatedAnnotations),
    instance.delete(removedAnnotations),
  ])

  return await instance.exportPDF()
}
